import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Mail, Phone, MapPin, CheckCircle2, XCircle, HelpCircle, Loader2, StickyNote, ExternalLink, FileText, RotateCcw, RotateCw, MessageSquare } from "lucide-react";
import { VideoUploader } from "@/components/video/VideoUploader";
import {
  getApplication, fetchAdminMediaBlob, rotateAdminMedia, getApprovalEmailPreview, setApplicationStatus, addApplicationNote, errMsg, adminMessages, adminSendMessage, formatPhone, resendApprovalEmail,
} from "@/lib/applyApi";

function ApprovalEmailNotice({ log, onResend, busy }) {
  const last = [...(log || [])].reverse().find((n) => n.type === "approval_email");
  if (!last || last.status === "sent") return null;
  return (
    <div data-testid="approval-email-warning" className="mt-3 rounded-lg border border-orange-200 bg-orange-50 p-3 text-sm text-orange-900">
      {last.status === "skipped" ? (last.error?.startsWith("Admin") ? "You chose not to email the seller." : "The seller was not emailed because this is a TEST application.") : `The approval email to the seller failed: ${last.error || "unknown error"}`}
      <button type="button" data-testid="resend-approval-email" onClick={onResend} disabled={busy}
        className="ml-2 inline-flex items-center h-8 px-3 rounded-full bg-primary text-white text-xs font-semibold disabled:bg-stone-200 disabled:text-stone-500">{busy ? "Sending…" : last.status === "skipped" ? "Send approval email" : "Resend approval email"}</button>
    </div>
  );
}
import { MessageThread } from "@/components/MessageThread";
import { SaleScheduling } from "@/components/SaleScheduling";
import { TestModeBanner, TestBadge } from "@/components/TestMode";
import { AgreementAdmin } from "@/components/AgreementAdmin";
import { AdTemplateAdminCard } from "@/components/AdTemplateStatus";
import { AppProgress } from "@/pages/Applications";
import { AdvertisingTermsPicker, validFee } from "@/components/AdvertisingTermsPicker";
import { locationLabel, prefLabel, addressLabel, registrationLabel } from "@/lib/geo";
import { disclosureRows } from "@/components/apply/HealthFields";
import { StatusPill, SourceBadge } from "@/pages/Applications";
import { AdminRegCheck } from "@/components/AdminRegCheck";
import { AdminTrainerCard } from "@/components/AdminTrainerCard";
import { BlobImg } from "@/components/apply/MediaStep";
import { MobileMenu, openSellerDashboard } from "@/components/AppNav";
import { AppHeader } from "@/components/brand/AppHeader";
import { genderLabel, breedLabel, disciplineLabels, priceLabel } from "@/lib/horseOptions";
import { Button } from "@/components/ui/button";
import { dayMT, fmtMT } from "@/lib/mountainTime";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";

const fmtDT = (v) => (v ? fmtMT(v) : "—");
const AWAITING = ["submitted", "under_review", "more_info_requested"];
const ACTIONS = {
  more_info_requested: ["Request More Info", HelpCircle, "border-violet-700 text-violet-700"],
  approved: ["Approve", CheckCircle2, "bg-emerald-700 hover:bg-emerald-800 text-white border-emerald-700"],
  declined: ["Decline", XCircle, "border-red-600 text-red-600"],
};

function Info({ rows }) {
  return (
    <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
      {rows.map(([k, v]) => (
        <div key={k} className={k === "Description" ? "sm:col-span-2" : ""}>
          <dt className="text-[11px] uppercase tracking-wider text-stone-400 font-semibold">{k}</dt>
          <dd className="text-stone-900 whitespace-pre-wrap break-words">{v || "—"}</dd>
        </div>
      ))}
    </dl>
  );
}

function Photo({ appId, m, readOnly }) {
  const [rot, setRot] = useState(m.rotation || 0);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => fetchAdminMediaBlob(appId, m.id, rot), [appId, m.id, rot]);
  const rotate = async (dir) => {
    setBusy(true);
    try { setRot((await rotateAdminMedia(appId, m.id, dir)).rotation); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not rotate photo"); }
    finally { setBusy(false); }
  };
  const open = async () => window.open(URL.createObjectURL(await load()), "_blank", "noopener");
  if (m.content_type === "application/pdf") {
    return (
      <button type="button" data-testid={`admin-photo-${m.id}`} onClick={open} className="relative aspect-square rounded-lg border border-stone-200 bg-stone-50 flex flex-col items-center justify-center gap-1 p-1">
        <FileText className="h-7 w-7 text-red-700" /><span className="text-[10px] font-semibold truncate max-w-full">{m.original_filename || "PDF"}</span>
      </button>
    );
  }
  return (
    <div className="relative aspect-square rounded-lg overflow-hidden border border-stone-200 group">
      <button type="button" data-testid={`admin-photo-${m.id}`} onClick={open} className="h-full w-full">
        <BlobImg load={load} alt={m.original_filename} className="h-full w-full object-cover group-hover:scale-105 transition-transform" />
        <ExternalLink className="absolute top-1 right-1 h-4 w-4 text-white drop-shadow" />
      </button>
      {m.kind === "photo" && !readOnly && <div className="absolute bottom-1 inset-x-1 flex justify-between">
        <button type="button" aria-label="Rotate left" data-testid={`admin-photo-rotate-ccw-${m.id}`} disabled={busy} onClick={() => rotate("ccw")} className="h-7 w-7 rounded-full bg-black/60 text-white flex items-center justify-center"><RotateCcw className="h-4 w-4" /></button>
        <button type="button" aria-label="Rotate right" data-testid={`admin-photo-rotate-cw-${m.id}`} disabled={busy} onClick={() => rotate("cw")} className="h-7 w-7 rounded-full bg-black/60 text-white flex items-center justify-center"><RotateCw className="h-4 w-4" /></button>
      </div>}
    </div>
  );
}

export default function ApplicationDetail() {
  return <ApplicationReview />;
}

// The one application viewer: full page at /applications/:id, or embedded in the Pipeline V2 Horse Workspace.
export function ApplicationReview({ appId, embedded = false, readOnly = false, canDecide = false, onChanged, onLeave }) {
  const params = useParams();
  const id = appId || params.id;
  const navigate = useNavigate();
  const fromPipeline = useLocation().state?.from === "pipeline";
  const [a, setA] = useState(null);
  const [busy, setBusy] = useState(null);
  const [dialog, setDialog] = useState(null);
  const [rejectFlow, setRejectFlow] = useState(false);
  const [message, setMessage] = useState("");
  const [note, setNote] = useState("");
  const [terms, setTerms] = useState({ fee: "10" });
  const [notifyTest, setNotifyTest] = useState(null);
  const [emailPreview, setEmailPreview] = useState(null);
  const openEmailPreview = async () => {
    try { setEmailPreview(await getApprovalEmailPreview(id)); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not load the email preview"); }
  };
  const [resending, setResending] = useState(false);
  const resend = async () => {
    setResending(true);
    try { setA(await resendApprovalEmail(id)); toast.success("Approval email sent to the seller"); }
    catch (e) { toast.error(e.response?.data?.detail || "Approval email could not be sent"); getApplication(id).then(setA).catch(() => {}); }
    finally { setResending(false); }
  };
  const signed = a?.contract_status === "signed" || (a?.contract_status || "").startsWith("amendment");

  useEffect(() => {
    getApplication(id).then(setA).catch(() => toast.error("Could not load application"));
  }, [id]);
  const [threadKey, setThreadKey] = useState(0);
  const loadMsgs = useCallback((o) => adminMessages(id, o), [id]);
  const sendMsg = useCallback((t) => adminSendMessage(id, t), [id]);

  const act = async (status, msg) => {
    setBusy(status);
    try {
      setA(await setApplicationStatus(id, status, msg, {
        ...(status === "approved" && !signed ? { advertising_fee_percent: Number(terms.fee) } : {}),
        ...(notifyTest !== null && (status !== "approved" || !signed) ? { notify_seller: notifyTest } : {}),
      }));
      setThreadKey((k) => k + 1);
      setNotifyTest(null);
      onChanged?.();
      toast.success(signed && status === "approved" ? "Approved status restored — agreement unchanged" : { approved: "Application approved", declined: "Application declined", more_info_requested: "More information requested" }[status]);
      setDialog(null);
      setRejectFlow(false);
      setMessage("");
    } catch (e) {
      toast.error(errMsg(e, "Could not update status"));
    } finally {
      setBusy(null);
    }
  };

  const saveNote = async () => {
    setBusy("note");
    try {
      setA(await addApplicationNote(id, note));
      setNote("");
    } catch (e) {
      toast.error(errMsg(e, "Could not save note"));
    } finally {
      setBusy(null);
    }
  };

  if (!a) return <div className={`${embedded ? "py-10" : "min-h-screen"} flex items-center justify-center`}><Loader2 className="h-6 w-6 animate-spin text-stone-400" /></div>;
  const c = a.contact || {};
  const cp = a.account_contact_prefs?.contact_preference ? a.account_contact_prefs : c;
  const h = a.horse || {};
  const photos = (a.media || []).filter((m) => m.kind === "photo");
  const papers = (a.media || []).filter((m) => m.kind === "registration_paper");

  return (
    <div data-testid={embedded ? "workspace-application-review" : undefined} className={embedded ? "min-w-0" : "min-h-screen bg-[#FAF8F5] pb-32 lg:pb-10 overflow-x-clip"}>
      {embedded ? (
        <div className="flex flex-wrap items-center gap-1.5 mb-3">
          <StatusPill status={a.status} />
          <SourceBadge source={a.source} testId="application-source-badge" />
          {a.is_test && <TestBadge testId="application-test-badge" />}
        </div>
      ) : <>
      <AppHeader title={h.horse_name || "Unnamed horse"} titleTestId="application-horse-name" eyebrow="Application" onBack={() => (fromPipeline ? navigate("/pipeline") : navigate("/applications"))} backTestId={fromPipeline ? "back-to-pipeline" : "back-to-applications"}
        backLabel={fromPipeline ? "Back to Horse Workspace" : "Back"} width="max-w-5xl"
        below={
          <div className="bg-white border-b border-stone-200">
            <div className="max-w-5xl mx-auto px-4 sm:px-8 py-2 flex flex-wrap items-center gap-1.5">
              <StatusPill status={a.status} />
              <SourceBadge source={a.source} testId="application-source-badge" />
              {a.is_test && <TestBadge testId="application-test-badge" />}
            </div>
          </div>
        }>
          <MobileMenu />
      </AppHeader>

      <TestModeBanner />
      </>}
      <main className={embedded ? "grid gap-4" : "max-w-5xl mx-auto px-4 sm:px-8 py-5 grid lg:grid-cols-[1fr_320px] gap-5"}>
        {embedded && canDecide && (AWAITING.includes(a.status) && !signed ? (
          <div data-testid="workspace-app-decision" className="rounded-xl border border-amber-200 bg-amber-50 p-3">
            <p className="text-sm font-semibold text-stone-900">Awaiting approval</p>
            <div className="mt-2 grid sm:grid-cols-2 gap-2">
              <Button data-testid="workspace-app-approve" disabled={!!busy} onClick={() => { setRejectFlow(false); setDialog("approved"); setNotifyTest(true); }}
                className="h-11 rounded-full bg-emerald-700 hover:bg-emerald-800 text-white"><CheckCircle2 className="h-4 w-4 mr-1.5" />Approve Application</Button>
              <Button data-testid="workspace-app-reject" variant="outline" disabled={!!busy} onClick={() => { setRejectFlow(true); setDialog("more_info_requested"); setNotifyTest(a.is_test ? false : null); }}
                className="h-11 rounded-full bg-white"><MessageSquare className="h-4 w-4 mr-1.5" />Reject / Request Changes</Button>
            </div>
          </div>
        ) : a.status === "approved" ? (
          <p data-testid="workspace-app-approved" className="flex items-center gap-1.5 rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm font-semibold text-emerald-800"><CheckCircle2 className="h-4 w-4" />Application Approved</p>
        ) : null)}
        <div className="space-y-5 min-w-0">
          <section className="bg-white border border-stone-200 rounded-xl p-4 sm:p-5">
            <h2 className="font-semibold text-stone-900 mb-3">Seller Contact</h2>
            <div className="text-base font-semibold text-stone-900">{c.full_name}</div>
            <div className="mt-2 flex flex-col sm:flex-row sm:flex-wrap gap-2">
              <a data-testid="seller-email" href={`mailto:${c.email}`} className="inline-flex items-center gap-2 min-h-[44px] sm:min-h-0 text-sm text-amber-900 break-all"><Mail className="h-4 w-4 shrink-0" />{c.email}</a>
              <a data-testid="seller-phone" href={`tel:${c.phone}`} className="inline-flex items-center gap-2 min-h-[44px] sm:min-h-0 text-sm text-amber-900"><Phone className="h-4 w-4" />{formatPhone(c.phone)}</a>
              {addressLabel(c) && <span data-testid="seller-address" className="inline-flex items-start gap-2 text-sm text-stone-600 whitespace-pre-line"><MapPin className="h-4 w-4 mt-0.5 shrink-0" />{addressLabel(c)}</span>}
              {!c.street_address && (c.region || c.country) && <span className="inline-flex items-center gap-2 text-sm text-stone-600"><MapPin className="h-4 w-4" />{[c.region, c.country].filter(Boolean).join(", ")}</span>}
            </div>
            <div data-testid="seller-contact-pref" className="mt-2 text-xs text-stone-600">
              Prefers: <strong data-testid="seller-account-pref">{prefLabel(cp.contact_preference) || "Not set"}</strong>
              {a.account_contact_prefs?.contact_preference && c.contact_preference && cp.contact_preference !== c.contact_preference && ` (account setting; on application: ${prefLabel(c.contact_preference)})`}
              {cp.sms_consent ? ` · SMS consent ${dayMT(cp.sms_consent_at)}` : cp.contact_preference && cp.contact_preference !== "email" ? " · no SMS consent" : ""}
              {c.promo_consent ? ` · Promotional consent ${dayMT(c.promo_consent_at)}` : " · No promotional consent"}
              {cp.sms_opted_out_at && !cp.sms_consent && " · opted out of texts"}
            </div>
            {a.consent?.privacy && (
              <div data-testid="privacy-consent-record" className="mt-1 text-xs text-stone-600">
                Privacy Policy acknowledged{a.consent.privacy_policy_version ? <> · <strong>Version {a.consent.privacy_policy_version}</strong></> : " (no published version at the time)"} · {fmtMT(a.consent.privacy_accepted_at || a.consent.at)}
              </div>
            )}
          </section>

          <section className="bg-white border border-stone-200 rounded-xl p-4 sm:p-5">
            <h2 className="font-semibold text-stone-900 mb-3">Horse Information</h2>
            <Info rows={[["Horse's Barn Name", h.horse_name], ["Registration", registrationLabel(h) + (h.registration_source ? ` · ${h.registration_source === "ai" ? "read by AI, seller confirmed" : "entered manually by seller"}` : "")], ["Age", h.age], ["Gender", genderLabel(h.gender)], ["Breed", breedLabel(h)], ["Primary Discipline", disciplineLabels(h).join(", ")],
              ["Trainer's Name", h.trainer_name], ["Trainer's Phone Number", h.trainer_phone], ["Trainer's Email", h.trainer_email],
              ["Expected Price", priceLabel(h)], ["Location", locationLabel(h)], ["Description", h.description], ...disclosureRows(h)]} />
          </section>

          {/* LIVE records stay view-only, except trainer access which authorized admins may manage. */}
          <AdminTrainerCard a={a} readOnly={readOnly && !canDecide} reload={() => getApplication(id).then(setA).catch(() => {})} />

          <section className="bg-white border border-stone-200 rounded-xl p-4 sm:p-5">
            <h2 className="font-semibold text-stone-900 mb-3">Photos ({photos.length})</h2>
            {photos.length ? (
              <div data-testid="admin-photos" className="grid grid-cols-3 sm:grid-cols-4 gap-2">{photos.map((m) => <Photo key={m.id} appId={a.id} m={m} readOnly={readOnly} />)}</div>
            ) : <p className="text-sm text-stone-500">No photos uploaded.</p>}
            <div className="mt-4"><VideoUploader scope={{ type: "app", id: a.id }} readOnly adminView /></div>
          </section>

          <section data-testid="admin-papers-section" className="bg-white border border-stone-200 rounded-xl p-4 sm:p-5">
            <h2 className="font-semibold text-stone-900 mb-1">Registration Papers ({papers.length})</h2>
            <p className="text-xs text-stone-400 mb-3">Private until the ad is approved and posted; registration papers are included in the ad after the horse photos.</p>
            {papers.length ? (
              <div data-testid="admin-papers" className="grid grid-cols-3 sm:grid-cols-4 gap-2">{papers.map((m) => m.content_type === "application/pdf"
                ? <a key={m.id} data-testid={`admin-paper-pdf-${m.id}`} href={`${process.env.REACT_APP_BACKEND_URL}/api/applications/${a.id}/media/${m.id}/file`} target="_blank" rel="noopener noreferrer" className="aspect-square rounded-lg border border-stone-200 bg-stone-50 flex flex-col items-center justify-center text-xs text-red-700 font-semibold p-1 text-center break-all">PDF<span className="text-stone-600 font-normal">{m.original_filename}</span></a>
                : <Photo key={m.id} appId={a.id} m={m} />)}</div>
            ) : <p className="text-sm text-stone-500">{h.registered === "yes" ? "No registration papers uploaded." : "Not applicable (horse not registered or not answered)."}</p>}
            {papers.length > 0 && <AdminRegCheck papers={papers} horse={h} />}
          </section>
        </div>

        <aside className="space-y-5 min-w-0">
          <fieldset disabled={readOnly} data-testid={readOnly ? "application-readonly-fieldset" : undefined} className="space-y-5 min-w-0 disabled:opacity-90">
          <MessageThread key={threadKey} viewer="admin" load={loadMsgs} send={sendMsg} title="Messages with Seller" />
          <SaleScheduling app={a} readOnly={readOnly} onChange={(d) => { setA(d); setThreadKey((k) => k + 1); onChanged?.(); }} />
          <AgreementAdmin app={a} refreshKey={threadKey} onChanged={() => setThreadKey((k) => k + 1)} />
          <AdTemplateAdminCard a={a} />
          <section className="bg-white border border-stone-200 rounded-xl p-4">
            <h2 className="font-semibold text-stone-900 mb-3">Decision</h2>
            {readOnly ? (
              <p data-testid="application-actions-readonly" className="text-xs text-stone-600">LIVE record — view only in Pipeline V2{canDecide ? " (approval controls are at the top of this tab)" : ""}. <a href={`/applications/${a.id}`} data-testid="application-readonly-open-page" onClick={(e) => { e.preventDefault(); onLeave?.(); navigate(`/applications/${a.id}`, { state: { from: "pipeline" } }); }} className="underline font-medium text-stone-800">Open the application page</a> to act on it.</p>
            ) : (
            <div data-testid="application-actions" className={embedded ? "grid grid-cols-2 gap-2" : "fixed lg:static bottom-0 inset-x-0 z-40 bg-white/95 lg:bg-transparent border-t lg:border-0 border-stone-200 p-3 lg:p-0 pb-[calc(0.75rem+env(safe-area-inset-bottom))] lg:pb-0 grid grid-cols-2 gap-2"}>
              {Object.entries(ACTIONS).filter(([s]) => !signed || s === "approved").map(([s, [l, Icon, tone]]) => (
                <Button key={s} data-testid={`action-${s}`} variant="outline" disabled={!!busy || a.status === s || a.status === "draft"}
                  onClick={() => (signed ? act("approved") : s === "more_info_requested" || s === "declined" || s === "approved" ? (setDialog(s), setNotifyTest(s === "approved" ? true : a.is_test ? false : null)) : act(s))}
                  className={`h-11 rounded-full text-xs sm:text-sm ${tone} ${signed ? "col-span-2" : ""}`}>
                  {busy === s ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Icon className="h-4 w-4 mr-1" />{signed ? (a.status === "approved" ? "Approved" : "Restore Approved") : l}</>}
                </Button>
              ))}
            </div>
            )}
            {signed && <p data-testid="signed-lock-note" className="mt-3 text-xs text-stone-500">Agreement signed — the application can't be moved back. Restoring Approved never creates or changes an agreement.</p>}
            {a.status === "approved" && (
              <>
                <ApprovalEmailNotice log={a.notification_log} onResend={resend} busy={resending} />
                <p className="mt-3 text-xs text-stone-500">Approved. Manage the Advertising Agreement below.</p>
                <AppProgress r={a} i="detail" />
                <Button data-testid="open-seller-dashboard" variant="outline" onClick={() => openSellerDashboard(`/dashboard/applications/${a.id}#agreement`)}
                  className="mt-2 w-full h-10 rounded-full"><ExternalLink className="h-4 w-4 mr-1.5" />Open Seller Dashboard</Button>
                <p className="mt-1 text-[11px] text-stone-400">Opens in a new tab as the signed-in account. Shows this horse only if that account's email is {c.email}.</p>
              </>
            )}
            {a.info_request_message && a.status === "more_info_requested" && (
              <p className="mt-3 text-xs text-violet-800 bg-violet-50 border border-violet-200 rounded p-2">Requested: {a.info_request_message}</p>
            )}
          </section>

          <section className="bg-white border border-stone-200 rounded-xl p-4">
            <h2 className="font-semibold text-stone-900 mb-1 flex items-center gap-2"><StickyNote className="h-4 w-4 text-amber-800" /> Internal Notes</h2>
            <p className="text-[11px] text-stone-400 mb-2">Private — never shown to the seller.</p>
            <div data-testid="notes-list" className="space-y-2 mb-3">
              {(a.internal_notes || []).map((n) => (
                <div key={n.id} className="text-sm bg-amber-50/60 border border-amber-100 rounded p-2">
                  <div className="whitespace-pre-wrap break-words text-stone-800">{n.text}</div>
                  <div className="mt-1 text-[11px] text-stone-400">{n.by} · {fmtDT(n.at)}</div>
                </div>
              ))}
            </div>
            <Textarea data-testid="note-input" rows={3} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add a private note…" className="text-base sm:text-sm" />
            <Button data-testid="add-note-button" onClick={saveNote} disabled={!note.trim() || busy === "note"} className="mt-2 w-full h-11 rounded-full">Add Note</Button>
          </section>

          <section className="bg-white border border-stone-200 rounded-xl p-4">
            <h2 className="font-semibold text-stone-900 mb-2">History</h2>
            <ol data-testid="status-history" className="space-y-1.5 text-xs text-stone-600">
              {(a.status_history || []).map((s, i) => (
                <li key={i}><StatusPill status={s.status} /> <span className="ml-1">{fmtDT(s.at)} · {s.by}</span>{s.message && <div className="mt-0.5 text-stone-500">“{s.message}”</div>}</li>
              ))}
              {(a.notification_log || []).map((n, i) => (
                <li key={`n${i}`} data-testid={`notification-log-${i}`}>{{ more_info_email: "More-info email", decline_email: "Decline email" }[n.type] || "Approval email"} {n.status === "sent" ? `sent to ${n.to}` : n.status === "skipped" ? (n.error?.startsWith("Admin") ? "not sent (unticked)" : "not sent (TEST)") : `FAILED${n.error ? ` — ${n.error}` : ""}`} <span className="ml-1 text-stone-500">{fmtDT(n.at)} · {n.by}</span></li>
              ))}
            </ol>
            {(a.registration_history || []).length > 0 && (
              <ol data-testid="registration-history" className="mt-2 space-y-1 text-xs text-stone-600 border-t border-stone-100 pt-2">
                {a.registration_history.map((x) => <li key={x.at}>Registration {x.action} ({x.source === "ai" ? "AI-read" : "manual"}): {x.registered_name} #{x.registered_number}{x.action === "corrected" ? ` (was ${x.previous_name || "—"} #${x.previous_number || "—"})` : ""} · {fmtDT(x.at)} · {x.by}</li>)}
              </ol>
            )}
            {(a.corrections || []).length > 0 && (
              <ol data-testid="corrections-history" className="mt-2 space-y-1 text-xs text-stone-600 border-t border-stone-100 pt-2">
                {a.corrections.map((x) => <li key={x.at}>Seller corrected {x.fields.join(", ") || "details"} · {fmtDT(x.at)} · {x.by}</li>)}
              </ol>
            )}
          </section>
          </fieldset>
        </aside>
      </main>

      <Dialog open={!!emailPreview} onOpenChange={(o) => !o && setEmailPreview(null)}>
        <DialogContent data-testid="approval-email-preview" className="w-[calc(100%-1.5rem)] max-w-lg rounded-xl">
          <DialogHeader>
            <DialogTitle>Approval email preview</DialogTitle>
            <DialogDescription>Preview only. Nothing is sent until you approve the application.</DialogDescription>
          </DialogHeader>
          {emailPreview && (
            <div className="space-y-2 text-sm">
              <p data-testid="approval-email-preview-to"><span className="text-stone-500">To:</span> {emailPreview.to || "No email on file"}</p>
              <p data-testid="approval-email-preview-subject"><span className="text-stone-500">Subject:</span> {emailPreview.subject}</p>
              {emailPreview.is_test && <p data-testid="approval-email-preview-test-note" className="text-amber-700">TEST application: this email is only sent if “Email the seller the approval email” is ticked (it is by default).</p>}
              <iframe title="Approval email" data-testid="approval-email-preview-frame" srcDoc={emailPreview.html} sandbox="" className="w-full h-80 rounded-md border border-stone-200 bg-white" />
            </div>
          )}
          <DialogFooter><Button data-testid="approval-email-preview-close" variant="outline" onClick={() => setEmailPreview(null)}>Close</Button></DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={!!dialog} onOpenChange={(o) => { if (!o) { setDialog(null); setNotifyTest(null); setRejectFlow(false); } }}>
        <DialogContent data-testid="action-dialog" className="w-[calc(100%-1.5rem)] rounded-xl">
          <DialogHeader>
            <DialogTitle>{dialog && ACTIONS[dialog][0]}</DialogTitle>
            <DialogDescription>
              {dialog === "more_info_requested" && "Write the message to the seller explaining what you need. It is posted to the seller's dashboard Messages, the seller is emailed a link to their dashboard (LIVE; TEST only if ticked below), and the status becomes More Information Requested."}
              {dialog === "declined" && "Decline this application? Your reason (optional) is posted to the seller's dashboard — a standard message is used if left blank — and the seller is emailed (LIVE; TEST only if ticked below)."}
              {dialog === "approved" && "Approve this horse and set its Advertising Agreement terms. The agreement is created for the seller to complete and sign in their dashboard. You can change the terms until the seller signs."}
            </DialogDescription>
          </DialogHeader>
          {rejectFlow && dialog !== "approved" && (
            <div role="radiogroup" aria-label="Reject or request changes" data-testid="reject-choice" className="grid grid-cols-2 gap-2">
              {[["more_info_requested", "Request Changes"], ["declined", "Reject Application"]].map(([s, l]) => (
                <button key={s} type="button" role="radio" aria-checked={dialog === s} data-testid={`reject-choice-${s}`} onClick={() => setDialog(s)}
                  className={`h-10 rounded-full border text-sm font-semibold ${dialog === s ? "bg-stone-900 border-stone-900 text-white" : "bg-white border-stone-300 text-stone-700"}`}>{l}</button>
              ))}
            </div>
          )}
          {dialog === "approved" && <AdvertisingTermsPicker fee={terms.fee} onChange={setTerms} autoDefault />}
          {notifyTest !== null && (dialog !== "approved" || !signed) && (
            <label data-testid="approve-notify-test" className="flex items-start gap-2 text-sm text-stone-700">
              <input type="checkbox" data-testid="approve-notify-test-checkbox" checked={notifyTest} onChange={(e) => setNotifyTest(e.target.checked)} className="mt-0.5 h-4 w-4 accent-stone-600" />
              <span>{dialog === "approved" ? "Email the seller the approval email" : "Email the seller anyway (TEST application)"}
                {dialog === "approved" && a.is_test && <span data-testid="approve-notify-test-warning" className="block text-xs text-amber-700">TEST application — this sends a real email to the seller's address.</span>}
              </span>
            </label>
          )}
          {dialog === "approved" && !signed && (
            <button type="button" data-testid="approve-email-preview-btn" onClick={openEmailPreview} className="self-start text-sm font-medium text-stone-700 underline underline-offset-2 hover:text-stone-900">
              Preview the seller approval email
            </button>
          )}
          {dialog !== "approved" && (
            <Textarea data-testid="action-message" rows={4} value={message} onChange={(e) => setMessage(e.target.value)}
              placeholder={dialog === "more_info_requested" ? "e.g. Please send a side photo and registration papers." : "Reason shown to the seller (optional)"} className="text-base sm:text-sm" />
          )}
          <DialogFooter className="gap-2">
            <Button data-testid="action-cancel" variant="outline" onClick={() => { setDialog(null); setNotifyTest(null); setRejectFlow(false); }} className="h-11 rounded-full">Cancel</Button>
            <Button data-testid="action-confirm" disabled={!!busy || (dialog === "more_info_requested" && !message.trim()) || (dialog === "approved" && !validFee(terms.fee))} onClick={() => act(dialog, message)}
              className="h-11 rounded-full">Confirm</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
